import { ThemeProvider } from '@fluentui/react';
import { DarkTheme } from '@fluentui/theme-samples';
import { IPrimePharmacyComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import React, { useEffect, useMemo, useState } from 'react';


// (Removed duplicate interface and import block)
import { IRadialMenuItem, RadialMenu } from 'components/controls/RadialMenu';
import { IMarkerSet } from 'views/BlueRelationship/Compare/Compare.constants';
import {
    BENEFIT_COMPARISON_PANEL_ID,
    GROUP_NUMBER_COMPARISON_PANEL_ID,
    QUICK_NAV_FLOATING_BACKDROP_HIDDEN,
    QUICK_NAV_FLOATING_BACKDROP_VISIBLE
} from './PharmacyPlan.constants';

interface IPharmacyQuickNavOverlayProps {
    primePharmacyModel: IPrimePharmacyComparisonVM | null;
    isHidden: boolean;
    markers: IMarkerSet;
    onNavigate: (links: string[]) => void;
    showDifferencesOnly: boolean;
    onDismiss?: () => void;
}

const PharmacyRadialNavOverlay: React.FC<IPharmacyQuickNavOverlayProps> = React.memo((props) => {
    const {
        primePharmacyModel,
        isHidden,
        markers,
        onNavigate,
        showDifferencesOnly,
        onDismiss,
    } = props;

    const [mountOnce, setMountOnce] = useState(false);
    const hasBenefitComparison = Boolean(primePharmacyModel?.prime);
    const hasGroupNumberComparison = (primePharmacyModel?.groupNumbers?.entities?.length ?? 0) > 0;

    useEffect(() => {
        // add keydown event listener to close the panel when press esc, and clean up the event listener when the component unmounts or when onDismiss changes
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onDismiss?.();
            }
        };

        if (onDismiss && !isHidden) {
            window.addEventListener('keydown', handleKeyDown);
        };

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onDismiss, isHidden]);

    useEffect(() => {
        // performance optimization: only mount the content when the panel is visible, and add a delay to make sure the animation is smooth
        if (!isHidden && !mountOnce) {
            const timer = window.setTimeout(() => {
                setMountOnce(true);
            }, 100);

            return () => window.clearTimeout(timer);
        }

        return undefined;
    }, [isHidden, mountOnce]);

    const menuItems: IRadialMenuItem[] = useMemo(() => {
        const items: IRadialMenuItem[] = [];

        // Benefit Comparison section
        if (hasBenefitComparison && primePharmacyModel?.prime) {
            items.push({
                key: 'benefit-comparison',
                label: 'Benefit Comparison',
                highlight: primePharmacyModel.prime.changed,
                getItems: async () => {
                    const benefitTabs: IRadialMenuItem[] = [];
                    const primeId = primePharmacyModel.prime.uniqueId;

                    benefitTabs.push({
                        key: 'benefit-properties',
                        label: 'Properties',
                        highlight: primePharmacyModel.prime.propertiesVM.changed,
                        onClick: () => onNavigate([BENEFIT_COMPARISON_PANEL_ID, primeId, `${primeId}-properties`]),
                    });

                    primePharmacyModel.prime.tabsVM.forEach((tab) => {
                        const tabId = tab.tabDefinition.tab.bcbs_config_tabid!;
                        const tabName = tab.tabDefinition.tab.bcbs_name ?? 'Tab';

                        benefitTabs.push({
                            key: `benefit-tab-${tabId}`,
                            label: tabName,
                            highlight: tab.changed,
                            getItems: async () => {
                                return tab.sectionsVM.map((section) => {
                                    const sectionId = section.sectionDefinition.section.bcbs_config_sectionid!;
                                    const sectionName = section.sectionDefinition.section.bcbs_name ?? 'Section';
                                    return {
                                        key: `benefit-section-${sectionId}`,
                                        label: sectionName,
                                        highlight: section.changed,
                                        onClick: () => onNavigate([
                                            BENEFIT_COMPARISON_PANEL_ID,
                                            primeId,
                                            `${primeId}-${tabId}`,
                                            `${primeId}-${sectionId}`,
                                        ]),
                                    };
                                });
                            },
                            onClick: () => onNavigate([BENEFIT_COMPARISON_PANEL_ID, primeId, `${primeId}-${tabId}`]),
                        });
                    });

                    return benefitTabs;
                },
            });
        }

        // Group Numbers Comparison section
        if (hasGroupNumberComparison && primePharmacyModel?.groupNumbers) {
            items.push({
                key: 'group-number-comparison',
                label: 'Group Numbers Comparison',
                highlight: primePharmacyModel.groupNumbers.changed,
                getItems: async () => {
                    return primePharmacyModel.groupNumbers.entities.map((entity, gnIndex) => {
                        const groupNumberId = entity.uniqueId;

                        return {
                            key: `gn-entity-${gnIndex}`,
                            label: entity.friendlyName ?? `Group #${gnIndex + 1}`,
                            highlight: entity.changed,
                            getItems: async () => {
                                const entityTabs: IRadialMenuItem[] = [];

                                entityTabs.push({
                                    key: `gn-${gnIndex}-properties`,
                                    label: 'Properties',
                                    highlight: entity.propertiesVM.changed,
                                    onClick: () => onNavigate([
                                        GROUP_NUMBER_COMPARISON_PANEL_ID,
                                        groupNumberId,
                                        `${groupNumberId}-properties`,
                                    ]),
                                });

                                entity.tabsVM.forEach((tab) => {
                                    const tabId = tab.tabDefinition.tab.bcbs_config_tabid!;
                                    const tabName = tab.tabDefinition.tab.bcbs_name ?? 'Tab';

                                    entityTabs.push({
                                        key: `gn-${gnIndex}-tab-${tabId}`,
                                        label: tabName,
                                        highlight: tab.changed,
                                        getItems: async () => {
                                            return tab.sectionsVM.map((section) => {
                                                const sectionId = section.sectionDefinition.section.bcbs_config_sectionid!;
                                                const sectionName = section.sectionDefinition.section.bcbs_name ?? 'Section';
                                                return {
                                                    key: `gn-${gnIndex}-section-${sectionId}`,
                                                    label: sectionName,
                                                    highlight: section.changed,
                                                    onClick: () => onNavigate([
                                                        GROUP_NUMBER_COMPARISON_PANEL_ID,
                                                        groupNumberId,
                                                        `${groupNumberId}-${tabId}`,
                                                        `${groupNumberId}-${sectionId}`,
                                                    ]),
                                                };
                                            });
                                        },
                                        onClick: () => onNavigate([
                                            GROUP_NUMBER_COMPARISON_PANEL_ID,
                                            groupNumberId,
                                            `${groupNumberId}-${tabId}`,
                                        ]),
                                    });
                                });

                                return entityTabs;
                            },
                        };
                    });
                },
            });
        }

        return items;
    }, [primePharmacyModel, hasBenefitComparison, hasGroupNumberComparison, onNavigate]);

    return (
        <ThemeProvider theme={DarkTheme}>
            {/* Backdrop overlay */}
            <div
                style={isHidden ? QUICK_NAV_FLOATING_BACKDROP_HIDDEN : QUICK_NAV_FLOATING_BACKDROP_VISIBLE}
                onClick={props.onDismiss}
            />
            <RadialMenu items={menuItems} isOpen={!props.isHidden} onDismiss={props.onDismiss} />
        </ThemeProvider>
    );
});

export default PharmacyRadialNavOverlay;